/**
 * GenerationSettingsControls - Reusable aspect ratio, variants, and seed controls
 *
 * Used in both GlobalSettingsCard and BatchMatrixRowExpanded (DRY).
 */

import { cn } from '@/lib/utils';

const ASPECT_RATIOS = ['1:1', '16:9', '9:16', '4:3', '3:2'];
const VARIANTS = [1, 2, 3, 4, 5];

export interface GenerationSettingsControlsProps {
  aspectRatios: string[];
  variants: number;
  seedMode: 'auto' | 'fixed';
  onAspectRatiosChange: (aspectRatios: string[]) => void;
  onVariantsChange: (variants: number) => void;
  onSeedModeChange: (seedMode: 'auto' | 'fixed') => void;
  disabled?: boolean;
  hideAspectRatios?: boolean;
  hideVariants?: boolean;
}

export function GenerationSettingsControls({
  aspectRatios,
  variants,
  seedMode,
  onAspectRatiosChange,
  onVariantsChange,
  onSeedModeChange,
  disabled = false,
  hideAspectRatios = false,
  hideVariants = false,
}: GenerationSettingsControlsProps) {
  const handleAspectRatioToggle = (ratio: string) => {
    if (disabled) return;
    const updated = aspectRatios.includes(ratio)
      ? aspectRatios.filter((r) => r !== ratio)
      : [...aspectRatios, ratio];
    onAspectRatiosChange(updated.length > 0 ? updated : ['1:1']);
  };

  return (
    <div className="space-y-3">
      {/* Aspect Ratios */}
      {!hideAspectRatios && (
        <div className="space-y-2">
          <label className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
            Aspect Ratios
          </label>
          <div className="flex gap-2 flex-wrap">
            {ASPECT_RATIOS.map((ratio) => (
              <button key={ratio} type="button" onClick={() => handleAspectRatioToggle(ratio)} disabled={disabled}
                className={cn('px-3 py-1.5 rounded text-xs font-medium transition-all', aspectRatios.includes(ratio) ? 'bg-orange-500 text-white' : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-200 dark:hover:bg-neutral-700', disabled && 'opacity-50 cursor-not-allowed')}>
                {ratio}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Variants */}
      {!hideVariants && (
        <div className="space-y-2">
          <label className="text-xs font-medium text-neutral-700 dark:text-neutral-300">Variants</label>
          <div className="flex gap-2">
            {VARIANTS.map((v) => (
              <button key={v} type="button" onClick={() => onVariantsChange(v)} disabled={disabled}
                className={cn('w-10 h-10 rounded text-sm font-medium transition-all', variants === v ? 'bg-orange-500 text-white' : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-200 dark:hover:bg-neutral-700', disabled && 'opacity-50 cursor-not-allowed')}>
                {v}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Seed Mode */}
      <div className="space-y-2">
        <label className="text-xs font-medium text-neutral-700 dark:text-neutral-300">Seed Mode</label>
        <div className="inline-flex gap-2">
          <button type="button" onClick={() => onSeedModeChange('auto')} disabled={disabled}
            className={cn('px-4 py-2 rounded text-sm font-medium transition-all', seedMode === 'auto' ? 'bg-orange-500 text-white' : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-200 dark:hover:bg-neutral-700', disabled && 'opacity-50 cursor-not-allowed')}>
            Auto
          </button>
          <button type="button" onClick={() => onSeedModeChange('fixed')} disabled={disabled}
            className={cn('px-4 py-2 rounded text-sm font-medium transition-all', seedMode === 'fixed' ? 'bg-orange-500 text-white' : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-200 dark:hover:bg-neutral-700', disabled && 'opacity-50 cursor-not-allowed')}>
            Fix
          </button>
        </div>
      </div>
    </div>
  );
}
